import { useCallback, useEffect, useRef, useState } from "react"
import { Head } from "@inertiajs/react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { ChatComposer } from "@/components/member-story/ChatComposer"
import { ChatTranscript } from "@/components/member-story/ChatTranscript"
import { DraftReview } from "@/components/member-story/DraftReview"
import { InterviewHeader } from "@/components/member-story/InterviewHeader"
import { WelcomeScreen } from "@/components/member-story/WelcomeScreen"
import { useReadAloud } from "@/hooks/use-read-aloud"

// How often to ask whether the draft (or a rewrite) has landed. The job is a
// single LLM call, so this is seconds of waiting, not minutes.
const DRAFT_POLL_MS = 3_000

// When to stop waiting. A job that exhausted its retries, or discarded an
// unparseable response, changes nothing server-side — so without a deadline the
// member watches a spinner forever. Generous enough to cover three retries of a
// long article call.
const DRAFT_POLL_DEADLINE_MS = 180_000

function readCsrfToken() {
  if (typeof document === "undefined") return null
  return document.querySelector('meta[name="csrf-token"]')?.content || null
}

// Turns the server's answered-turn list into chat bubbles.
//
// Acknowledgments are deliberately absent here: they are conversational
// furniture, not part of the record the coach reads, so they are never
// persisted. A resumed interview redraws the questions and answers only.
function transcriptToTurns(transcript) {
  return transcript.flatMap((entry) => [
    { role: "bot", text: entry.questionText, isFollowup: entry.followupIndex > 0 },
    { role: "member", text: entry.answerText },
  ])
}

/**
 * The Member Story interview: welcome, conversation, draft — three phases of
 * one page, because they are one sitting.
 *
 * Which phase shows is derived from server state, never from a local step
 * counter: a member who closes the tab and comes back a day later lands exactly
 * where they left off (their answers are already saved).
 */
export default function MemberStoryInterviewShow({
  invite,
  questionCount,
  transcript,
  nextTurn,
  draft: initialDraft,
  publishedStoryCount = 0,
}) {
  const [status, setStatus] = useState(invite.status)
  const [turns, setTurns] = useState(() => {
    const history = transcriptToTurns(transcript)
    return nextTurn
      ? [ ...history, { role: "bot", text: nextTurn.text, isFollowup: nextTurn.isFollowup } ]
      : history
  })
  const [current, setCurrent] = useState(nextTurn)
  // The last question actually asked. `current` goes null while an answer is in
  // flight, and reading the header off it made the progress bar snap to 100%
  // between every turn.
  const lastPosition = useRef(nextTurn?.position || 1)
  if (current) lastPosition.current = current.position
  const [draft, setDraft] = useState(initialDraft)
  const [rewritesRemaining, setRewritesRemaining] = useState(invite.rewritesRemaining)
  const [thinking, setThinking] = useState(false)
  const [starting, setStarting] = useState(false)
  const [rewriting, setRewriting] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  // The draft/rewrite job stopped answering. Distinct from `error`: it is the
  // spinner that has failed, not a request the member just made.
  const [writingFailed, setWritingFailed] = useState(false)
  // Set when a change request went past the rewrite cap and was saved for the
  // coach — the member pressed a button and deserves to see it landed.
  const [noteSentToCoach, setNoteSentToCoach] = useState(false)

  const coach = {
    name: invite.coachName,
    avatarUrl: invite.coachAvatarUrl,
    initials: invite.coachInitials,
  }

  const readAloud = useReadAloud({ token: invite.token })
  // The question the member is looking at right now, so read-aloud fires once
  // per question rather than on every re-render.
  const spokenRef = useRef(null)

  const post = useCallback(
    async (action, body, { json = true } = {}) => {
      const response = await fetch(
        `/app/member/member_story_interviews/${invite.token}/${action}`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": readCsrfToken() || "",
            Accept: "application/json",
            ...(json ? { "Content-Type": "application/json" } : {}),
          },
          credentials: "same-origin",
          body: json ? JSON.stringify(body) : body,
        }
      )
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Something went wrong. Please try again.")
      return data
    },
    [invite.token]
  )

  // Read the current question aloud when the member has asked us to.
  useEffect(() => {
    if (!current || !readAloud.enabled) return
    const key = `${current.position}-${current.followupIndex}`
    if (spokenRef.current === key) return

    spokenRef.current = key
    readAloud.speak({
      text: current.text,
      position: current.isFollowup ? null : current.position,
    })
  }, [current, readAloud])

  // Poll while a draft or rewrite job is in flight. `writingFailed` is part of
  // the condition so a timed-out poll stays stopped instead of restarting.
  const writing = ((status === "started" && !current) || rewriting) && !writingFailed
  useEffect(() => {
    if (!writing) return undefined

    const startingVersion = draft?.version ?? 0
    const deadline = Date.now() + DRAFT_POLL_DEADLINE_MS

    const timer = setInterval(async () => {
      if (Date.now() > deadline) {
        // Give the member a way out rather than an endless spinner. Clearing
        // `rewriting` matters most: DraftReview disables "send to my coach"
        // while a rewrite is in flight, so a failed rewrite would otherwise
        // lock them out of handing the story over at all.
        setRewriting(false)
        setWritingFailed(true)
        return
      }

      try {
        const response = await fetch(
          `/app/member/member_story_interviews/${invite.token}/draft`,
          { headers: { Accept: "application/json" }, credentials: "same-origin" }
        )
        if (!response.ok) return

        const data = await response.json()
        if (data.draft && data.draft.version > startingVersion) {
          setDraft(data.draft)
          setStatus(data.invite.status)
          setRewritesRemaining(data.invite.rewritesRemaining)
          setRewriting(false)
        }
      } catch {
        // A dropped poll is not worth surfacing — the next tick tries again.
      }
    }, DRAFT_POLL_MS)

    return () => clearInterval(timer)
  }, [writing, draft?.version, invite.token])

  const handleStart = async () => {
    setStarting(true)
    setError(null)
    try {
      const data = await post("start", {})
      setStatus(data.invite.status)
      setCurrent(data.nextTurn)
      setTurns([
        ...(data.opening ? [ { role: "bot", text: data.opening, isAside: true } ] : []),
        ...(data.nextTurn
          ? [ { role: "bot", text: data.nextTurn.text, isFollowup: data.nextTurn.isFollowup } ]
          : []),
      ])
    } catch (caught) {
      setError(caught.message)
    } finally {
      setStarting(false)
    }
  }

  // Returns true when the answer was accepted. The composer keeps the member's
  // text on a false so a network blip does not cost them what they wrote.
  const handleSend = async (text, mode) => {
    const asked = current
    setTurns((previous) => [ ...previous, { role: "member", text } ])
    setCurrent(null)
    setThinking(true)
    setError(null)

    try {
      const data = await post("answers", { answer: text, mode })
      setStatus(data.invite.status)
      setCurrent(data.nextTurn)
      setTurns((previous) => [
        ...previous,
        // Often absent: the bot only says something back when the answer
        // earned it. Rendered as its own bubble ahead of the question, the way
        // a person would say it.
        ...(data.acknowledgment
          ? [ { role: "bot", text: data.acknowledgment, isAside: true } ]
          : []),
        ...(data.nextTurn
          ? [ { role: "bot", text: data.nextTurn.text, isFollowup: data.nextTurn.isFollowup } ]
          : []),
      ])
      return true
    } catch (caught) {
      // Put the question back. Without this `current` stays null, `writing`
      // flips true, the composer unmounts and the member is stranded on a
      // "writing your story" spinner over a failed request.
      setTurns((previous) => previous.slice(0, -1))
      setCurrent(asked)
      setError(caught.message)
      return false
    } finally {
      setThinking(false)
    }
  }

  const handleRequestChange = async (note) => {
    setError(null)
    setNoteSentToCoach(false)
    try {
      const data = await post("rewrites", { change_note: note })
      setRewritesRemaining(data.invite.rewritesRemaining)
      if (data.rewriting) {
        setWritingFailed(false)
        setRewriting(true)
      }
      // Past the rewrite cap the note goes to a person instead. The member
      // pressed a button labelled "send the note" — say that it arrived.
      if (data.noteSavedForCoach) setNoteSentToCoach(true)
    } catch (caught) {
      setError(caught.message)
    }
  }

  const handleRetryDraft = async () => {
    setError(null)
    setWritingFailed(false)
    try {
      const data = await post("redraft", {})
      if (data.draft) {
        setDraft(data.draft)
        setStatus(data.invite.status)
      }
    } catch (caught) {
      setError(caught.message)
      setWritingFailed(true)
    }
  }

  const handleSendToCoach = async ({ consent, headshot }) => {
    setSending(true)
    setError(null)
    try {
      const form = new FormData()
      form.append("consent", consent ? "true" : "false")
      if (headshot) form.append("headshot", headshot)

      const data = await post("send_to_coach", form, { json: false })
      setStatus(data.invite.status)
    } catch (caught) {
      setError(caught.message)
    } finally {
      setSending(false)
    }
  }

  const showWelcome = status === "sent"
  const showDraft = Boolean(draft) && !current
  // The chat is the only phase that pins its chrome. Welcome and draft review
  // are ordinary documents the member reads top to bottom, and trapping those
  // in a fixed-height column would give the draft its own inner scrollbar.
  const showChat = !showWelcome && !showDraft

  // The draft replaces a conversation the member has scrolled to the bottom of.
  // Landing them at the end of their own story, mid-sentence, reads as broken.
  useEffect(() => {
    if (showDraft) window.scrollTo({ top: 0, behavior: "auto" })
  }, [showDraft])

  return (
    <>
      <Head title="Your Member Story" />

      <div
        className={cn(
          "flex flex-col bg-zinc-50 font-sans",
          // 100dvh, not 100vh: on mobile Safari the URL bar otherwise pushes
          // the composer under the fold, which is the thing being fixed here.
          showChat ? "h-[100dvh] overflow-hidden" : "min-h-screen"
        )}
      >
        {!showWelcome && (
          <InterviewHeader
            position={current?.position || lastPosition.current}
            questionCount={questionCount}
            readAloud={readAloud}
            coach={coach}
            showProgress={!showDraft}
          />
        )}

        {showWelcome && (
          <WelcomeScreen
            coach={coach}
            questionCount={questionCount}
            publishedStoryCount={publishedStoryCount}
            starting={starting}
            onStart={handleStart}
          />
        )}

        {!showWelcome && showDraft && (
          <DraftReview
            draft={draft}
            memberFirstName={invite.memberFirstName}
            coachName={invite.coachName}
            rewritesRemaining={rewritesRemaining}
            rewriting={rewriting}
            rewriteFailed={writingFailed}
            noteSentToCoach={noteSentToCoach}
            sending={sending}
            sent={status === "sent_to_coach"}
            error={error}
            onRequestChange={handleRequestChange}
            onSend={handleSendToCoach}
          />
        )}

        {showChat && (
          <>
            <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-6">
              <ChatTranscript turns={turns} thinking={thinking} />

              {writing && (
                <div className="mt-8 flex flex-col items-center gap-3 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-kf-red" aria-hidden="true" />
                  <p className="text-sm text-zinc-600">
                    Thank you, {invite.memberFirstName} — give us a moment to put
                    your story together.
                  </p>
                </div>
              )}

              {writingFailed && !draft && (
                <div className="mt-8 flex flex-col items-center gap-4 text-center">
                  <p className="max-w-sm text-sm text-zinc-600">
                    Writing your story is taking longer than it should. Your
                    answers are all saved — nothing is lost.
                  </p>
                  <Button variant="kf-outline" onClick={handleRetryDraft}>
                    Try again
                  </Button>
                </div>
              )}

              {error && <p className="mt-4 text-center text-sm text-kf-red">{error}</p>}
            </main>

            {!writing && (
              <ChatComposer
                token={invite.token}
                disabled={thinking || !current}
                onSend={handleSend}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
